import * as React from 'react';
import {defineMessage} from 'react-intl';
import payment from './accept-a-payment';
import checkout from './checkout';
import billing from './billing';
import terminal from './terminal';

const CARD_DEMO = {
  element: 'single',
  buttonFont: 'Arial, sans-serif',
  buttonColor: '#ffffff',
  buttonBg: '#5469d4',
  cardFont: 'Arial, sans-serif',
  cardText: '#32325d',
  cardInvalid: '#fa755a',
};

const CHECKOUT_DEMO = {
  paymentMethods: ['card'],
};

const SERVER_AND_CLIENT = {
  platform: ['web', 'ios', 'android'],
  client: {
    web: ['html', 'react'],
    ios: ['swift', 'objc'],
    android: ['kotlin', 'javaclient'],
  },
  server: ['node', 'ruby', 'python', 'php', 'dotnet', 'java', 'go'],
};

const TYPE_GUIDE_HREF_MAP = {
  payments: '/docs/payments/accept-card-payments?ui=elements',
  checkout: '/docs/payments/accept-a-payment?ui=checkout',
  billing: '/docs/billing/subscriptions/checkout',
  terminal: '/docs/terminal/payments/setup-sdk',
};

export default {
  payments: {
    integrations: payment.integrations,
    sections: payment.sections,
    copy: {
      title: {
        message: defineMessage({
          id: 'docsApp.integrationBuilder.content.payments.copy.title',
          defaultMessage: 'Custom payment flow',
        }),
      },
      heading: {
        message: defineMessage({
          id: 'docsApp.integrationBuilder.content.payments.copy.heading',
          defaultMessage: 'Custom payment flow',
        }),
      },
      guideHref: TYPE_GUIDE_HREF_MAP.payments,
      body: {
        message: defineMessage({
          id: 'docsApp.integrationBuilder.content.payments.copy.body',
          defaultMessage:
            'Learn how to embed a custom Stripe payment form in your website or application. The client- and server-side code builds a checkout form to complete a card payment.',
        }),
      },
      bottom: {
        heading: {
          message: defineMessage({
            id:
              'docsApp.integrationBuilder.content.payments.copy.bottom.heading',
            defaultMessage: 'Congratulations!',
          }),
        },
        body: {
          message: defineMessage({
            id: 'docsApp.integrationBuilder.content.payments.copy.bottom.body',
            defaultMessage:
              'You are ready to accept card payments with Stripe. Continue with the steps below to add more features.',
          }),
        },
        subheading: {
          message: defineMessage({
            id:
              'docsApp.integrationBuilder.content.payments.copy.bottom.subheading',
            defaultMessage: 'After the payment',
          }),
        },
        nextSteps: [
          {
            heading: {
              message: defineMessage({
                id:
                  'docsApp.integrationBuilder.content.payments.copy.bottom.nextSteps.one.heading',
                defaultMessage: 'Payouts',
              }),
            },
            body: {
              message: defineMessage({
                id:
                  'docsApp.integrationBuilder.content.payments.copy.bottom.nextSteps.one.body',
                defaultMessage:
                  'Learn how to move funds out of your Stripe account into your bank account.',
              }),
            },
            href: '/docs/payouts',
          },
          {
            heading: {
              message: defineMessage({
                id:
                  'docsApp.integrationBuilder.content.payments.copy.bottom.nextSteps.two.heading',
                defaultMessage: 'Refunds',
              }),
            },
            body: {
              message: defineMessage({
                id:
                  'docsApp.integrationBuilder.content.payments.copy.bottom.nextSteps.two.body',
                defaultMessage:
                  'Handle requests for refunds by using the Stripe API or Dashboard.',
              }),
            },
            href: '/docs/refunds',
          },
          {
            heading: {
              message: defineMessage({
                id:
                  'docsApp.integrationBuilder.content.payments.copy.bottom.nextSteps.three.heading',
                defaultMessage: 'Fulfillment',
              }),
            },
            body: {
              message: defineMessage({
                id:
                  'docsApp.integrationBuilder.content.payments.copy.bottom.nextSteps.three.body',
                defaultMessage:
                  'Set up a webhook to fulfill orders after a payment succeeds. Webhooks are the most reliable way to handle business-critical events.',
              }),
            },
            href: '/docs/webhooks/quickstart',
          },
        ],
      },
    },
    demos: {
      web: {
        type: 'card',
        settings: CARD_DEMO,
      },
    },
    languages: {
      allowed: SERVER_AND_CLIENT,
      active: {},
    },
  },
  checkout: {
    integrations: checkout.integrations,
    sections: checkout.sections,
    copy: {
      title: {
        message: defineMessage({
          id: 'docsApp.integrationBuilder.content.checkout.copy.title',
          defaultMessage: 'Prebuilt Checkout page',
        }),
      },
      heading: {
        message: defineMessage({
          id: 'docsApp.integrationBuilder.content.checkout.copy.heading',
          defaultMessage: 'Prebuilt Checkout page',
        }),
      },
      guideHref: TYPE_GUIDE_HREF_MAP.checkout,
      body: {
        message: defineMessage({
          id: 'docsApp.integrationBuilder.content.checkout.copy.body',
          defaultMessage:
            'Explore a full, working code sample of an integration with <a>Stripe Checkout</a>. The client- and server-side code redirects to a prebuilt payment page hosted on Stripe.',
        }),
        values: {
          a: (str) => <a href="/docs/payments/checkout">{str}</a>,
        },
      },
      bottom: {
        heading: {
          message: defineMessage({
            id:
              'docsApp.integrationBuilder.content.checkout.copy.bottom.heading',
            defaultMessage: 'Congratulations!',
          }),
        },
        body: {
          message: defineMessage({
            id: 'docsApp.integrationBuilder.content.checkout.copy.bottom.body',
            defaultMessage:
              'You have a basic Checkout integration working. Learn how to customize the appearance of your checkout page.',
          }),
        },
        subheading: {
          message: defineMessage({
            id:
              'docsApp.integrationBuilder.content.checkout.copy.bottom.subheading',
            defaultMessage: 'After the payment',
          }),
        },
        nextSteps: [
          {
            heading: {
              message: defineMessage({
                id:
                  'docsApp.integrationBuilder.content.checkout.copy.bottom.nextSteps.one.heading',
                defaultMessage: 'Fulfillment',
              }),
            },
            body: {
              message: defineMessage({
                id:
                  'docsApp.integrationBuilder.content.checkout.copy.bottom.nextSteps.one.body',
                defaultMessage:
                  'Set up a webhook to fulfill orders after a payment succeeds. Webhooks are the most reliable way to handle business-critical events.',
              }),
            },
            href: '/docs/payments/checkout/fulfill-orders',
          },
          {
            heading: {
              message: defineMessage({
                id:
                  'docsApp.integrationBuilder.content.checkout.copy.bottom.nextSteps.two.heading',
                defaultMessage: 'Payouts',
              }),
            },
            body: {
              message: defineMessage({
                id:
                  'docsApp.integrationBuilder.content.checkout.copy.bottom.nextSteps.two.body',
                defaultMessage:
                  'Learn how to move funds out of your Stripe account into your bank account.',
              }),
            },
            href: '/docs/payouts',
          },
          {
            heading: {
              message: defineMessage({
                id:
                  'docsApp.integrationBuilder.content.checkout.copy.bottom.nextSteps.three.heading',
                defaultMessage: 'Refunds',
              }),
            },
            body: {
              message: defineMessage({
                id:
                  'docsApp.integrationBuilder.content.checkout.copy.bottom.nextSteps.three.body',
                defaultMessage:
                  'Handle requests for refunds by using the Stripe API or Dashboard.',
              }),
            },
            href: '/docs/refunds',
          },
        ],
      },
    },
    languages: {
      allowed: {
        platform: ['web'],
        client: {
          web: ['html', 'react', 'next'],
        },
        server: ['node', 'ruby', 'python', 'php', 'dotnet', 'java', 'go'],
      },
      active: {},
    },
    demos: {
      web: {
        type: 'checkout',
        settings: CHECKOUT_DEMO,
      },
    },
  },
  billing: {
    integrations: billing.integrations,
    sections: billing.sections,
    copy: {
      title: {
        message: defineMessage({
          id: 'docsApp.integrationBuilder.content.billing.copy.title',
          defaultMessage: 'Subscription Checkout',
        }),
      },
      heading: {
        message: defineMessage({
          id: 'docsApp.integrationBuilder.content.billing.copy.heading',
          defaultMessage: 'Prebuilt subscription page with Stripe Checkout',
        }),
      },
      guideHref: TYPE_GUIDE_HREF_MAP.billing,
      body: {
        message: defineMessage({
          id: 'docsApp.integrationBuilder.content.billing.copy.body',
          defaultMessage:
            'Build a full, working subscription integration using <c>Stripe Billing</c> and <a>Stripe Checkout</a> and your test mode data. Stripe Billing APIs let you create and manage subscriptions, invoices, and recurring payments. Checkout provides the prebuilt, secure, Stripe-hosted UI for collecting payment details. <d></d><d></d>In the downloaded sample app, the client- and server-side code redirects customers from your site to a prebuilt payment page hosted on Stripe. The builder injects data, like your pricing model, into the sample code as you follow the steps.',
        }),
        values: {
          a: (str) => <a href="/docs/payments/checkout">{str}</a>,
          b: (str) => (
            <a href="/docs/billing/subscriptions/customer-portal">{str}</a>
          ),
          c: (str) => <a href="/docs/billing">{str}</a>,
          d: () => <br />,
        },
      },
      bottom: {
        heading: {
          message: defineMessage({
            id:
              'docsApp.integrationBuilder.content.billing.copy.bottom.heading',
            defaultMessage: 'Congratulations!',
          }),
        },
        body: {
          message: defineMessage({
            id: 'docsApp.integrationBuilder.content.billing.copy.bottom.body',
            defaultMessage:
              'You have a basic Checkout with subscriptions integration working. Learn how to customize the subscription options.',
          }),
        },
        subheading: {
          message: defineMessage({
            id:
              'docsApp.integrationBuilder.content.billing.copy.bottom.subheading',
            defaultMessage: 'See also',
          }),
        },
        nextSteps: [
          {
            heading: {
              message: defineMessage({
                id:
                  'docsApp.integrationBuilder.content.billing.copy.bottom.nextSteps.one.heading',
                defaultMessage: 'Subscription overview',
              }),
            },
            body: {
              message: defineMessage({
                id:
                  'docsApp.integrationBuilder.content.billing.copy.bottom.nextSteps.one.body',
                defaultMessage: 'Learn how subscriptions work within Stripe.',
              }),
            },
            href: '/docs/billing/subscriptions/overview',
          },
          {
            heading: {
              message: defineMessage({
                id:
                  'docsApp.integrationBuilder.content.billing.copy.bottom.nextSteps.three.heading',
                defaultMessage: 'Prorations',
              }),
            },
            body: {
              message: defineMessage({
                id:
                  'docsApp.integrationBuilder.content.billing.copy.bottom.nextSteps.three.body',
                defaultMessage: 'Learn about prorations.',
              }),
            },
            href: '/docs/billing/subscriptions/prorations',
          },
          {
            heading: {
              message: defineMessage({
                id:
                  'docsApp.integrationBuilder.content.billing.copy.bottom.nextSteps.four.heading',
                defaultMessage: 'Upsells',
              }),
            },
            body: {
              message: defineMessage({
                id:
                  'docsApp.integrationBuilder.content.billing.copy.bottom.nextSteps.four.body',
                defaultMessage:
                  'Upsell customers to longer billing intervals and collect more revenue.',
              }),
            },
            href: '/docs/payments/checkout/upsells',
          },
          {
            heading: {
              message: defineMessage({
                id:
                  'docsApp.integrationBuilder.content.billing.copy.bottom.nextSteps.two.heading',
                defaultMessage: 'Additional features',
              }),
            },
            body: {
              message: defineMessage({
                id:
                  'docsApp.integrationBuilder.content.billing.copy.bottom.nextSteps.two.body',
                defaultMessage:
                  'In addition to basic subscription functionality, there are a number of other features you can use to customize your integration. You can offer free trials and discounts, configure future changes to subscriptions, and more.',
              }),
            },
            href: '/docs/billing/subscriptions/additional-features',
          },
        ],
      },
    },
    languages: {
      allowed: {
        platform: ['web'],
        client: {
          web: ['html', 'react'],
        },
        server: ['node', 'ruby', 'python', 'php', 'dotnet', 'java', 'go'],
      },
      active: {},
    },
  },
  terminal: {
    title: {
      message: defineMessage({
        id: 'docsApp.integrationBuilder.content.terminal.title',
        defaultMessage: 'Accept in-person payments',
      }),
    },
    integrations: terminal.integrations,
    sections: terminal.sections,
    copy: {
      title: {
        message: defineMessage({
          id: 'docsApp.integrationBuilder.content.terminal.copy.title',
          defaultMessage: 'Accept in-person payments',
        }),
      },
      heading: {
        message: defineMessage({
          id: 'docsApp.integrationBuilder.content.terminal.copy.heading',
          defaultMessage: 'Accept in-person payments',
        }),
      },
      guideHref: TYPE_GUIDE_HREF_MAP.terminal,
      body: {
        message: defineMessage({
          id: 'docsApp.integrationBuilder.content.terminal.copy.body',
          defaultMessage:
            'Set up Stripe Terminal and use the simulated reader to emulate accepting in-person payments.',
        }),
      },
      bottom: {
        heading: {
          message: defineMessage({
            id:
              'docsApp.integrationBuilder.content.terminal.copy.bottom.heading',
            defaultMessage: 'Congratulations!',
          }),
        },
        body: {
          message: defineMessage({
            id: 'docsApp.integrationBuilder.content.terminal.copy.bottom.body',
            defaultMessage:
              'Your Terminal integration is now set up to collect in-person payments. Next, test your current integration with a physical reader or integrate Stripe Terminal with your connect platform.',
          }),
        },
        subheading: {
          message: defineMessage({
            id:
              'docsApp.integrationBuilder.content.terminal.copy.bottom.subheading',
            defaultMessage: 'Next steps',
          }),
        },
        nextSteps: [
          {
            heading: {
              message: defineMessage({
                id:
                  'docsApp.integrationBuilder.content.terminal.copy.bottom.nextSteps.one.heading',
                defaultMessage: 'Connecting to a Reader',
              }),
            },
            body: {
              message: defineMessage({
                id:
                  'docsApp.integrationBuilder.content.terminal.copy.bottom.nextSteps.one.body',
                defaultMessage:
                  'Learn what it means to connect your app to a reader.',
              }),
            },
            href: '/docs/terminal/payments/connect-reader',
          },
          {
            heading: {
              message: defineMessage({
                id:
                  'docsApp.integrationBuilder.content.terminal.copy.bottom.nextSteps.two.heading',
                defaultMessage: 'Fleet Management',
              }),
            },
            body: {
              message: defineMessage({
                id:
                  'docsApp.integrationBuilder.content.terminal.copy.bottom.nextSteps.two.body',
                defaultMessage:
                  'Group and manage a fleet of readers by physical location.',
              }),
            },
            href: '/docs/terminal/fleet/locations',
          },
          {
            heading: {
              message: defineMessage({
                id:
                  'docsApp.integrationBuilder.content.terminal.copy.bottom.nextSteps.three.heading',
                defaultMessage: 'Connect',
              }),
            },
            body: {
              message: defineMessage({
                id:
                  'docsApp.integrationBuilder.content.terminal.copy.bottom.nextSteps.three.body',
                defaultMessage:
                  'Integrate Stripe Terminal with your Connect platform.',
              }),
            },
            href: '/docs/terminal/payments/connect',
          },
        ],
      },
    },
    languages: {
      allowed: {
        country: ['US', 'CA', 'GB', 'IE'],
        reader: {
          US: ['wpe', 'chipper'],
          CA: ['wpe', 'wp3'],
          GB: ['wpe', 'wp3'],
          IE: ['wpe', 'wp3'],
        },
        platform: {
          wpe: {
            US: ['web', 'ios', 'android'],
            CA: ['web', 'ios', 'android'],
            GB: ['web', 'ios'],
            IE: ['web', 'ios'],
          },
          chipper: {
            US: ['ios', 'android'],
            CA: ['ios', 'android'],
            GB: ['ios', 'android'],
            IE: ['ios', 'android'],
          },
          wp3: {
            US: ['ios', 'android'],
            CA: ['ios', 'android'],
            GB: ['ios', 'android'],
            IE: ['ios', 'android'],
          },
        },
        client: {
          web: ['javascript', 'esnext'],
          ios: ['swift', 'objc'],
          android: ['kotlin', 'javaclient'],
        },
        server: ['node', 'ruby', 'python', 'php', 'dotnet', 'java', 'go'],
      },
      active: {},
    },
  },
};
